import { ArrowUpRight, Ban, CheckCircle2, Copy, Hourglass, Link2, Trash2, X } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

import { Alert, ButtonGroup, DecisionStamp } from 'common-src/components/base';
import { getCurrentRoleId, getIsSuperUser } from 'common-src/features/auth';
import { getDashboardClients, getRoleById } from 'common-src/models/Client';
import { getDashboardV2Appointments } from 'common-src/models/DashboardV2Appointment';
import { getDashboardV2Tasks } from 'common-src/models/DashboardV2Task';
import { isFeatureEnabled } from 'common-src/models/FeatureFlag';
import { openDocument } from 'common-src/models/PatientDocument';
import { getTaskReasonById } from 'common-src/models/PatientTask';
import { TaskType } from 'common-src/models/PatientTask/constants';

import {
  getCareOrchestrationCapacityTasks,
  getCareOrchestrationCapacityTasksLoaded,
} from 'src/features/careOrchestration';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';

import { getStaffRole } from '../../balanceAssign';
import {
  BLOCKED_REASON_LABELS,
  BLOCKED_REASON_OPTIONS,
  BLOCKED_STATUS,
  ESCALATED_TO_OPTIONS,
  MANUAL_TASK_CANCEL_FEATURE_FLAG,
  pathwayLabel,
  PRIORITY_BADGE_COPY,
  PRIORITY_SORT_ORDER,
  TASK_DISPLAY_STATUS,
  teamMeta,
} from '../../constants';
import { buildCortexAskUrl, CLAIM_STATE } from '../../cortexLinks';
import {
  buildLinkedTaskStructure,
  canMarkTaskComplete,
  copyTextToClipboard,
  dayName,
  formatClinicianName,
  formatClock12,
  formatDue,
  getClientByAthenaProviderId,
  getDelegatedSubtaskMeta,
  getLinkedTaskWaitingForTitle,
  getPatient,
  getTaskAssessmentTypeLabel,
  getTaskAssessmentVariantLabel,
  getTaskCancelDecisionPayload,
  getTaskDecisionOptions,
  getTaskDeepLinkUrl,
  getTaskDisplayStatus,
  getTaskDisplayStatusLabel,
  getTaskDisplayTitle,
  getTaskPatientId,
  getTaskStateName,
  isAssessmentTask,
  isAutomaticWorkflowTask,
  isLinkedTask,
  isMessageTask,
  isTaskAwaitingRealCallback,
  isTaskCanceled,
  isTaskCaregiverAssisted,
  isTaskEscalatedStatus,
  isTaskReadinessComplete,
  isTaskUnableToComplete,
  isWorkflowBlockedTask,
  monthDay,
  parseReadinessCalendarDate,
} from '../../helpers';
import { detectNewBreach, projectClaim } from '../../hudSelectors';
import AthenaLink from '../AthenaLink';
import { Badge } from '../Badge';
import CompleteWithSubtasksModal from '../CompleteWithSubtasksModal';
import CortexLink from '../CortexLink';
import DelegateSubtasksModal from '../DelegateSubtasksModal';
import EscalateTaskModal from '../EscalateTaskModal';
import { HuddleNotesContext, HuddleNotesPanel, HuddleNotesSummary } from '../HuddleNotes';
import IntakeLink from '../IntakeLink';
import LeadResourceCallForm from '../LeadResourceCallForm';
import PlatformLink from '../PlatformLink';
import ProviderAvatars from '../ProviderAvatars';
import ReassignModal from '../ReassignModal';
import TaskConversation from '../TaskConversation';
import {
  BackBar,
  BriefField,
  BriefItem,
  BriefRow,
  clinicVisitTimes,
  FactSection,
  ItemLink,
  PanelHeadline,
  visitCode,
  VisitCodeBadge,
  visitLabelWithoutCode,
  VisitSections,
} from '../Visit';
import backBarStyles from '../Visit/BackBar.module.scss';
import styles from './TaskDetail.module.scss';

const isTaskFollowedByCurrentUser = (task, currentUserId) => {
  const normalizedCurrentUserId = `${currentUserId ?? ''}`.trim();
  if (!normalizedCurrentUserId) return false;

  return (task?.followedBy || []).some(
    (followerId) => `${followerId ?? ''}`.trim() === normalizedCurrentUserId,
  );
};

const INTAKE_ASSIST_REQUESTED_REASON_ID = 88;
const INTAKE_COMPLETED_REVIEW_REASON_ID = 86;

const getTaskNotesVisit = (task, linkedAppointment, nextLoadedVisit, appointments) => {
  if (linkedAppointment) return linkedAppointment;
  if (task?.externalObjectType === 'athena_appointment_id' && task?.externalObjectId) {
    return { id: task.externalObjectId };
  }
  if (nextLoadedVisit) return nextLoadedVisit;
  if (!task?.patientId) return null;
  const today = new Date().toISOString().slice(0, 10);
  return (
    appointments.find(
      (appointment) =>
        `${appointment?.patientId}` === `${task.patientId}` && appointment?.date >= today,
    ) || null
  );
};

const TaskDetail = ({
  task,
  relatedTasks,
  cachedDelegatedSubtasks,
  onClose,
  onBackToReadiness,
  readinessAppointmentLabel,
  readinessStatus,
  onAssignToMe,
  isAssigning,
  onUnassign,
  isUnassigning,
  onMarkComplete,
  isMarking,
  onCancelTask,
  isCanceling,
  onReassign,
  isReassigning,
  onFollowTask,
  isFollowing,
  onUnfollowTask,
  isUnfollowing,
  onBlockTask,
  isBlocking,
  onUnblockTask,
  isUnblocking,
  onEscalateTask,
  isEscalating,
  onDeescalateTask,
  isDeescalating,
  onDelegateSubtasks,
  isDelegatingSubtasks,
  onCancelSubtask,
  cancelingSubtaskId,
  currentUserId,
  currentUserName,
  cortexOrigin,
  onReplySent,
  onSelectTask,
  departmentLabelById = {},
}) => {
  const { showAlert, AlertType } = useAlert();
  const [showReassignModal, setShowReassignModal] = React.useState(false);
  const [showDelegateSubtasksModal, setShowDelegateSubtasksModal] = React.useState(false);
  const [showEscalateModal, setShowEscalateModal] = React.useState(false);
  const [showCompleteWithSubtasksModal, setShowCompleteWithSubtasksModal] = React.useState(false);
  const [pendingCompletionPayload, setPendingCompletionPayload] = React.useState(null);
  const [showCancelReason, setShowCancelReason] = React.useState(false);
  const [cancelReason, setCancelReason] = React.useState('');
  const [showBlockDropdown, setShowBlockDropdown] = React.useState(false);
  const [currentOwner, setCurrentOwner] = React.useState(task?.owner || '');
  const [wasJustClaimed, setWasJustClaimed] = React.useState(false);
  const [confirmingBreach, setConfirmingBreach] = React.useState(false);
  const [hoveringClaim, setHoveringClaim] = React.useState(false);
  const [isFollowed, setIsFollowed] = React.useState(
    isTaskFollowedByCurrentUser(task, currentUserId),
  );
  const [loadedDelegatedSubtasks, setLoadedDelegatedSubtasks] = React.useState([]);
  const blockDropdownRef = React.useRef(null);
  const [showHuddleNotes, setShowHuddleNotes] = React.useState(false);
  const {
    notesByApptId,
    huddleNoteVisitsByPatientId,
    huddleVisitLabelById,
    huddleNextVisitByPatientId,
    onPostNote,
    onEditNote,
  } = React.useContext(HuddleNotesContext);

  React.useEffect(() => {
    setShowHuddleNotes(false);
  }, [task?.id]);

  if (!task) return null;

  const patient = task?.patient ?? getPatient(task?.patientId);
  const team = teamMeta[task.team];
  const clients = useCustomSelector((state) => getDashboardClients(state)) || [];
  const appointments = useCustomSelector(getDashboardV2Appointments) || [];
  const allTasks = useCustomSelector(getDashboardV2Tasks) || [];
  const relationshipTasks = Array.isArray(relatedTasks) ? relatedTasks : allTasks;
  const capacityTasks = useCustomSelector(getCareOrchestrationCapacityTasks) || [];
  const capacityTasksLoaded = useCustomSelector(getCareOrchestrationCapacityTasksLoaded);
  const canUseManualTaskCancel = useCustomSelector((state) =>
    isFeatureEnabled(state, MANUAL_TASK_CANCEL_FEATURE_FLAG),
  );
  const canUseAdminOnlyActions = useCustomSelector(
    (state) => getCurrentRoleId(state) === 1 || getIsSuperUser(state),
  );
  const due = task?.due ? formatDue(task.due) : { text: '—', urgent: false };
  const dueDateText = task?.due ? String(task.due).split('T')[0] : '—';
  const pathway = pathwayLabel[task.pathway] ?? task.pathway;
  const normalizedCurrentOwner = `${currentOwner || ''}`.trim().toLowerCase();
  const isUnassigned = !normalizedCurrentOwner || normalizedCurrentOwner === 'unassigned';
  const isClaimedByYou =
    !!`${currentUserName || ''}`.trim() &&
    `${currentOwner || ''}`.trim().toLowerCase() ===
      `${currentUserName || ''}`.trim().toLowerCase();
  const currentUserClient = React.useMemo(
    () => clients.find((client) => `${client?.id ?? ''}` === `${currentUserId ?? ''}`) || null,
    [clients, currentUserId],
  );
  const claimProjection = React.useMemo(() => {
    if (!capacityTasksLoaded || !isUnassigned || !currentUserName) return null;
    return projectClaim(
      capacityTasks,
      task,
      'day',
      currentUserName,
      getStaffRole(currentUserClient),
      currentUserClient?.dailyCapacityMinutes,
      currentUserClient?.weeklyCapacityMinutes,
    );
  }, [capacityTasks, capacityTasksLoaded, task, isUnassigned, currentUserName, currentUserClient]);
  const claimBreach = React.useMemo(() => {
    if (!capacityTasksLoaded || !isUnassigned || !currentUserName) return { hasNewBreach: false };
    return detectNewBreach(
      capacityTasks,
      task,
      currentUserName,
      getStaffRole(currentUserClient),
      currentUserClient?.dailyCapacityMinutes,
    );
  }, [capacityTasks, capacityTasksLoaded, task, isUnassigned, currentUserName, currentUserClient]);
  const claimIsOverCapacity = !!claimProjection && claimProjection.projected.util >= 0.85;
  const assignedClientRole = React.useMemo(() => {
    const owner = `${currentOwner || ''}`.trim().toLowerCase();
    if (!owner) return '';

    const matchingClient = clients.find((client) => {
      const fullName =
        typeof client?.getName === 'function'
          ? client.getName(false, true)
          : `${client?.firstName || ''} ${client?.lastName || ''}`.trim();
      const basicName = `${client?.firstName || ''} ${client?.lastName || ''}`.trim();

      return fullName.toLowerCase() === owner || basicName.toLowerCase() === owner;
    });

    if (!matchingClient) return '';
    return (
      matchingClient?.role || (matchingClient?.roleId ? getRoleById(matchingClient.roleId) : '')
    );
  }, [clients, currentOwner]);
  const getClientNameById = React.useCallback(
    (clientId) => {
      const normalizedClientId = Number(clientId);
      if (!Number.isInteger(normalizedClientId)) return null;

      const client = clients.find((candidateClient) => candidateClient?.id === normalizedClientId);
      if (!client) return null;

      if (typeof client.getName === 'function') {
        return client.getName(false, true);
      }

      return `${client?.firstName || ''} ${client?.lastName || ''}`.trim() || null;
    },
    [clients],
  );
  const assignedTo = isUnassigned ? 'Unassigned' : `${currentOwner} (${assignedClientRole || '—'})`;
  const parentTask = React.useMemo(
    () =>
      relationshipTasks.find(
        (candidateTask) =>
          `${candidateTask?.id || ''}`.trim() === `${task?.parentTaskId || ''}`.trim(),
      ) || null,
    [relationshipTasks, task?.parentTaskId],
  );
  // Null whenever no identifier-free situation text can be built for this task,
  // or the org has no Cortex corpus — the card is hidden, never disabled.
  const cortexAskUrl = React.useMemo(() => {
    const claimState = isClaimedByYou
      ? CLAIM_STATE.ClaimedByMe
      : isUnassigned
        ? CLAIM_STATE.Unclaimed
        : CLAIM_STATE.ClaimedByOther;

    return buildCortexAskUrl(task, { origin: cortexOrigin, claimState, parentTask });
  }, [task, cortexOrigin, isClaimedByYou, isUnassigned, parentTask]);
  const parentTaskTitle = parentTask ? getTaskDisplayTitle(parentTask) : '';
  const parentTaskSummary = `${
    parentTask?.taskDescription || parentTask?.task_description || parentTask?.caption || ''
  }`.trim();
  const parentTaskOwner = `${parentTask?.owner || ''}`.trim() || 'Unassigned';
  const parentTaskOwnerInitials = parentTaskOwner
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
  const parentTaskTeam = parentTask ? teamMeta[parentTask?.team] : null;
  const parentTaskDueDateText = parentTask?.due ? String(parentTask.due).split('T')[0] : '';
  const reasonLabel = getTaskReasonById(task?.reasonId)?.label || '-';
  const createdByLabel = task?.workflowInstanceId
    ? 'Workflow (system)'
    : getClientNameById(task?.createdBy) || '-';
  const linkedAppointment = appointments.find(
    (appointment) => `${appointment?.id || ''}`.trim() === `${task?.externalObjectId || ''}`.trim(),
  );
  const patientNoteVisits = huddleNoteVisitsByPatientId[`${getTaskPatientId(task)}`] || [];
  const latestNoteVisit = patientNoteVisits[patientNoteVisits.length - 1];
  const notesVisit =
    getTaskNotesVisit(
      task,
      linkedAppointment,
      huddleNextVisitByPatientId[`${getTaskPatientId(task)}`],
      appointments,
    ) || (latestNoteVisit ? { id: latestNoteVisit.id } : null);
  const notesVisitId = notesVisit ? `${notesVisit.id}` : null;
  const showVisitLabels =
    patientNoteVisits.length > 1 ||
    (patientNoteVisits.length === 1 && patientNoteVisits[0].id !== notesVisitId);
  const taskHuddleNotes = patientNoteVisits.length
    ? patientNoteVisits.flatMap((visit) =>
        visit.notes.map((note) => ({
          ...note,
          visitLabel: showVisitLabels ? visit.label : undefined,
        })),
      )
    : notesVisitId
      ? notesByApptId[notesVisitId] || []
      : [];
  const clinicianClient = getClientByAthenaProviderId(clients, linkedAppointment?.providerAthenaId);
  const clinician = formatClinicianName(clinicianClient);
  const taskVisit = linkedAppointment
    ? {
        code: visitCode({
          cptCode: linkedAppointment.cptCode,
          visitType: linkedAppointment.visitType,
        }),
        kindName: linkedAppointment.visitType,
        label: visitLabelWithoutCode(linkedAppointment.visitType),
        times: clinicVisitTimes(linkedAppointment),
      }
    : null;
  const taskVisitWhen = taskVisit
    ? [
        `${dayName(taskVisit.times.date)}, ${monthDay(taskVisit.times.date)}`,
        [formatClock12(taskVisit.times.start), taskVisit.times.zone].filter(Boolean).join(' '),
      ].join(' · ')
    : '';
  const taskVisitLocation = linkedAppointment
    ? departmentLabelById[`${linkedAppointment.departmentAthenaId ?? ''}`] ||
      linkedAppointment.location ||
      ''
    : '';
  const taskDeepLinkUrl = getTaskDeepLinkUrl(task);
  const handleCopyTaskLink = async () => {
    try {
      const didCopy = await copyTextToClipboard(taskDeepLinkUrl);
      if (!didCopy) throw new Error('Copy failed');
      showAlert(AlertType.Success, '', 'Task link copied to clipboard.');
    } catch (error) {
      showAlert(AlertType.Error, '', 'Unable to copy task link.');
    }
  };
  const taskIsMessage = isMessageTask(task);
  const taskIsAssessment = isAssessmentTask(task);
  const taskIsPreVisitIntake = task?.typeId === TaskType.PreVisitIntake.value;
  const isAutomaticTask = isAutomaticWorkflowTask(task);
  const taskTitle = getTaskDisplayTitle(task);
  const taskDescription = `${task?.taskDescription || task?.task_description || ''}`.trim();
  const assessmentTypeLabel = getTaskAssessmentTypeLabel(task);
  const assessmentVariantLabel = getTaskAssessmentVariantLabel(task);
  const caregiverAssisted = isTaskCaregiverAssisted(task);
  const taskSop = task?.taskSop ?? task?.task_sop ?? '';
  const taskStateName = getTaskStateName(task);
  const isRegalOutreachTask =
    taskStateName === 'complete_regal_outreach' ||
    taskTitle.trim().toLowerCase() === 'complete first outreach attempt';
  const leadResourceId =
    isRegalOutreachTask &&
    (!task?.externalObjectType || task.externalObjectType === 'lead_resource_id')
      ? task?.externalObjectId
      : null;
  const displayStatusLabel = getTaskDisplayStatusLabel(task);
  const taskDecisionOptions = getTaskDecisionOptions(task);
  const hasDecisionActions = taskDecisionOptions.length > 0;
  const hasTaskSop = !!`${taskSop}`.trim();
  const assessmentReviewLink = task?.noteUrl ?? '';
  const hasAssessmentReviewLink = !!`${assessmentReviewLink}`.trim();
  const isCompletedTask = isTaskReadinessComplete(task);
  const isCanceledTask = isTaskCanceled(task);
  const isUnableToCompleteTask = isTaskUnableToComplete(task);
  const isBlockedStatus = task?.blockedStatus === BLOCKED_STATUS.Blocked;
  const isEscalatedStatus = isTaskEscalatedStatus(task);
  const blockedReasonLabel =
    task?.blockedReason != null ? (BLOCKED_REASON_LABELS[task.blockedReason] ?? 'Blocked') : null;
  const blockedByLabel = getClientNameById(task?.blockedBy);
  const blockedAtLabel = task?.blockedAt
    ? new Date(task.blockedAt).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      })
    : null;
  const escalatedByLabel = getClientNameById(task?.escalatedBy);
  const escalatedToUserLabel = getClientNameById(task?.escalatedToUser);
  const escalatedToOption = ESCALATED_TO_OPTIONS.find(
    (option) => option.value === Number(task?.escalatedTo),
  );

  const escalatedToLabel =
    escalatedToUserLabel || (escalatedToOption ? `${escalatedToOption.label} team` : null);

  const escalatedAtLabel = task?.escalatedAt
    ? new Date(task.escalatedAt).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      })
    : null;
  const isPrerequisiteBlockedTask = isLinkedTask(task);
  const isBlockedByDependencies = isWorkflowBlockedTask(task);
  const taskPriority = task?.priority;
  React.useEffect(() => {
    if (task?.parentTaskId || !task?.id) {
      setLoadedDelegatedSubtasks([]);
      return undefined;
    }

    setLoadedDelegatedSubtasks(
      Array.isArray(cachedDelegatedSubtasks) ? cachedDelegatedSubtasks : [],
    );
    return undefined;
  }, [cachedDelegatedSubtasks, task?.id, task?.parentTaskId]);

  const delegatedSubtasks = React.useMemo(() => {
    const parentTaskId = `${task?.id || ''}`.trim();
    if (!parentTaskId) return [];

    const mergedSubtasksById = new Map();

    loadedDelegatedSubtasks.forEach((subtask) => {
      const subtaskId = `${subtask?.id || ''}`.trim();
      if (!subtaskId) return;
      mergedSubtasksById.set(subtaskId, subtask);
    });

    relationshipTasks.forEach((candidateTask) => {
      if (`${candidateTask?.parentTaskId || ''}` !== parentTaskId) return;

      const subtaskId = `${candidateTask?.id || ''}`.trim();
      if (!subtaskId) return;
      mergedSubtasksById.set(subtaskId, candidateTask);
    });

    return Array.from(mergedSubtasksById.values()).filter(
      (candidateTask) => `${candidateTask?.parentTaskId || ''}` === parentTaskId,
    );
  }, [relationshipTasks, loadedDelegatedSubtasks, task?.id]);
  const delegatedSubtaskMeta = React.useMemo(
    () => getDelegatedSubtaskMeta(delegatedSubtasks),
    [delegatedSubtasks],
  );
  const openDelegatedSubtasks = delegatedSubtaskMeta.openSubtasks;
  const completedDelegatedSubtasksCount = delegatedSubtaskMeta.completedCount ?? 0;
  const canceledDelegatedSubtasksCount = delegatedSubtaskMeta.canceledCount ?? 0;
  const doneDelegatedSubtasksCount = delegatedSubtaskMeta.doneCount;
  const isWaitingOnSubtasks = !task?.parentTaskId && delegatedSubtaskMeta.waitingOnSubtasks;
  const areAllDelegatedSubtasksComplete = !task?.parentTaskId && delegatedSubtaskMeta.allDone;
  const canDelegateMoreSubtasks = isClaimedByYou && !task?.parentTaskId && !isAutomaticTask;
  const sortedDelegatedSubtasks = React.useMemo(
    () =>
      [...delegatedSubtasks].sort((taskA, taskB) => {
        const statusA = getTaskDisplayStatus(taskA) === TASK_DISPLAY_STATUS.Done ? 1 : 0;
        const statusB = getTaskDisplayStatus(taskB) === TASK_DISPLAY_STATUS.Done ? 1 : 0;

        if (statusA !== statusB) return statusA - statusB;

        return (
          new Date(taskA?.due || taskA?.taskAssignDueAt || 0) -
          new Date(taskB?.due || taskB?.taskAssignDueAt || 0)
        );
      }),
    [delegatedSubtasks],
  );
  const prerequisiteTaskTitle = React.useMemo(() => {
    if (!isPrerequisiteBlockedTask) return null;

    const sortedSiblingTasks = [...relationshipTasks].sort((taskA, taskB) => {
      const priorityA = PRIORITY_SORT_ORDER[taskA?.priority] ?? Number.MAX_SAFE_INTEGER;
      const priorityB = PRIORITY_SORT_ORDER[taskB?.priority] ?? Number.MAX_SAFE_INTEGER;
      if (priorityA !== priorityB) return priorityA - priorityB;
      return new Date(taskA?.due || 0) - new Date(taskB?.due || 0);
    });

    return getLinkedTaskWaitingForTitle(task, sortedSiblingTasks);
  }, [relationshipTasks, isPrerequisiteBlockedTask, task?.externalObjectId, task?.id]);
  const linkedChildTasks = React.useMemo(() => {
    const taskId = `${task?.id || ''}`;
    if (!taskId || task?.parentTaskId) return [];
    const { linkedChildTasksByParentId } = buildLinkedTaskStructure(relationshipTasks);
    return linkedChildTasksByParentId.get(taskId) || [];
  }, [relationshipTasks, task?.id, task?.parentTaskId]);

  const showMarkComplete =
    !isCompletedTask &&
    isClaimedByYou &&
    !taskIsMessage &&
    canMarkTaskComplete(task) &&
    !isTaskAwaitingRealCallback(task);
  const showCancelTask =
    canUseManualTaskCancel &&
    canUseAdminOnlyActions &&
    isClaimedByYou &&
    !taskIsMessage &&
    !isAutomaticTask;
  const showCompletionActions = showMarkComplete && !showCancelReason;

  const showBlockedIndicator =
    isPrerequisiteBlockedTask && !isCompletedTask && !showCompletionActions && !showCancelReason;

  React.useEffect(() => {
    setCurrentOwner(task?.owner || '');
    setWasJustClaimed(false);
    setConfirmingBreach(false);
    setHoveringClaim(false);
    setShowBlockDropdown(false);
    setShowEscalateModal(false);
    setShowCompleteWithSubtasksModal(false);
    setPendingCompletionPayload(null);
    setShowCancelReason(false);
    setCancelReason('');
  }, [task?.id]);

  React.useEffect(() => {
    setCurrentOwner(task?.owner || '');
  }, [task?.owner]);

  React.useEffect(() => {
    setIsFollowed(isTaskFollowedByCurrentUser(task, currentUserId));
  }, [currentUserId, task]);

  React.useEffect(() => {
    if (!showBlockDropdown) return undefined;

    const handleClickOutside = (event) => {
      if (blockDropdownRef.current?.contains(event.target)) return;
      setShowBlockDropdown(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showBlockDropdown]);

  const handleClaimTask = async () => {
    if (!task?.id) return;
    if (claimBreach.hasNewBreach && !confirmingBreach) {
      setConfirmingBreach(true);
      return;
    }
    setConfirmingBreach(false);
    const wasAssigned = (await onAssignToMe?.(task.id)) ?? false;
    if (!wasAssigned) return;
    setCurrentOwner(currentUserName || currentOwner);
    setWasJustClaimed(true);
  };

  const claimButtonLabel = (() => {
    if (isAssigning) return 'Claiming...';
    if (claimBreach.hasNewBreach)
      return confirmingBreach ? 'Confirm — Assign anyway' : 'Assign anyway';
    return 'Claim';
  })();

  const claimNote = (() => {
    if (!claimProjection) return null;
    const pct = Math.round(claimProjection.projected.util * 100);
    const projected = claimProjection.projected.committed;
    const delta = claimProjection.deltaMinutes;
    if (claimBreach.hasNewBreach) {
      const dueLabel = claimBreach.due ? formatDue(claimBreach.due).text : 'soon';
      return {
        tone: 'breach',
        text: `Will miss SLA — task due ${dueLabel} (~${claimBreach.projectedPct}% of that day's capacity).`,
      };
    }
    if (claimIsOverCapacity) {
      return {
        tone: 'over',
        text: `Projected ${projected}m · ${pct}% of today (+${delta}m) — over capacity.`,
      };
    }
    return { tone: 'ok', text: `Projected ${projected}m · ${pct}% of today (+${delta}m)` };
  })();

  const renderClaimNote = () => {
    if (!hoveringClaim || !claimNote) return null;
    const toneClass =
      claimNote.tone === 'breach'
        ? styles.claimNoteBreach
        : claimNote.tone === 'over'
          ? styles.claimNoteOver
          : styles.claimNoteOk;
    return <div className={`${styles.claimNote} ${toneClass}`}>{claimNote.text}</div>;
  };

  const claimDayLabel = (daysUntilDue) => {
    if (daysUntilDue < 0) return `${Math.abs(daysUntilDue)}d overdue`;
    if (daysUntilDue === 0) return 'today';
    if (daysUntilDue === 1) return 'tomorrow';
    return `in ${daysUntilDue} days`;
  };

  const renderCapacityWarningBanner = () => {
    if (!claimProjection || (!claimIsOverCapacity && !claimBreach.hasNewBreach)) return null;
    const isBreach = claimBreach.hasNewBreach;
    const pct = Math.round(claimProjection.projected.util * 100);
    const breachTask = isBreach
      ? (allTasks.find((t) => `${t?.id ?? ''}` === `${claimBreach.taskId ?? ''}`) ?? task)
      : null;

    return (
      <div
        className={`${styles.capacityWarningBanner} ${
          isBreach ? styles.capacityWarningBannerBreach : styles.capacityWarningBannerOver
        }`}
      >
        <div className={styles.capacityWarningTitle}>
          {isBreach ? 'Over capacity · SLA at risk' : 'Over capacity'}
        </div>
        <div className={styles.capacityWarningText}>
          Claiming puts you at <strong>{pct}%</strong> of today (
          {claimProjection.projected.committed}m, +{claimProjection.deltaMinutes}m).
        </div>
        {isBreach && (
          <div className={styles.capacityWarningSlaText}>
            Breaks the deadline on <strong>{getTaskDisplayTitle(breachTask)}</strong>{' '}
            {(claimBreach.daysUntilDue ?? 0) < 0 ? '' : 'due '}
            {claimDayLabel(claimBreach.daysUntilDue ?? 0)} (
            {claimBreach.due ? claimBreach.due.slice(0, 10) : ''}) — projected{' '}
            {claimBreach.projectedPct}% of that day&apos;s capacity.
          </div>
        )}
      </div>
    );
  };

  const handleFollowTask = async () => {
    if (!task?.id || isFollowing) return;
    const wasFollowed = (await onFollowTask?.(task.id)) ?? false;
    if (!wasFollowed) return;
    setIsFollowed(true);
  };

  const handleMarkCompleteClick = async (completionPayload = null) => {
    if (!task?.id || isMarking) return;

    if (!task?.parentTaskId && openDelegatedSubtasks.length > 0) {
      setPendingCompletionPayload(completionPayload);
      setShowCompleteWithSubtasksModal(true);
      return;
    }

    await onMarkComplete?.(task.id, completionPayload ?? undefined);
  };

  const handleCancelTaskClick = async () => {
    if (!task?.id || isCanceling) return;
    const normalizedReason = cancelReason.trim();
    if (!showCancelReason) {
      setShowCancelReason(true);
      return;
    }
    if (!normalizedReason) return;

    const wasCanceled =
      (await onCancelTask?.(task.id, normalizedReason, getTaskCancelDecisionPayload(task))) ??
      false;
    if (!wasCanceled) return;
    setCancelReason('');
    setShowCancelReason(false);
  };

  const handleUnfollowTask = async () => {
    if (!task?.id || isUnfollowing) return;
    const wasUnfollowed = (await onUnfollowTask?.(task.id)) ?? false;
    if (!wasUnfollowed) return;
    setIsFollowed(false);
  };

  const handleOpenAssessmentReview = () => {
    if (!hasAssessmentReviewLink) return;
    window.open(assessmentReviewLink, '_blank', 'noopener,noreferrer');
  };

  const handleBlockedButtonClick = () => {
    if (isUnblocking) return;
    if (isBlockedStatus) {
      onUnblockTask?.(task.id);
      return;
    }
    if (isBlocking || isEscalating) return;
    setShowBlockDropdown((prev) => !prev);
  };

  const handleBlockReasonSelect = async (blockedReason) => {
    const wasBlocked = (await onBlockTask?.(task.id, blockedReason)) ?? false;
    if (!wasBlocked) return;
    setShowBlockDropdown(false);
  };

  const handleEscalateButtonClick = () => {
    if (isEscalating || isDeescalating || isBlocking || isUnblocking) return;
    if (isEscalatedStatus) {
      onDeescalateTask?.(task.id);
      return;
    }
    setShowBlockDropdown(false);
    setShowEscalateModal(true);
  };

  const claimButtonProps = {
    className: `${styles.claimButton} ${claimBreach.hasNewBreach ? styles.claimButtonBreach : ''}`,
    disabled: isAssigning,
    onClick: handleClaimTask,
    onMouseEnter: () => setHoveringClaim(true),
    onMouseLeave: () => setHoveringClaim(false),
    onFocus: () => setHoveringClaim(true),
    onBlur: () => setHoveringClaim(false),
  };

  const systemBanner = !isCompletedTask && isAutomaticTask && (
    <div className={styles.systemBanner}>
      <div className={styles.systemBannerCopy}>
        <div className={styles.systemLabel}>Handled by system</div>
        <div className={styles.systemText}>
          This automatic workflow step updates itself when the background check finishes.
        </div>
      </div>
      {task?.callbackStatus ? <Badge variant="outline">{task.callbackStatus}</Badge> : null}
    </div>
  );

  const followingBanner = !isAutomaticTask && !isClaimedByYou && isFollowed && (
    <>
      <div className={styles.followingBanner}>
        <div className={styles.followingLabel}>You are following this task</div>
        <div className={styles.followingBannerActions}>
          {isUnassigned && (
            <button type="button" {...claimButtonProps}>
              {claimButtonLabel}
            </button>
          )}
          <button
            type="button"
            className={styles.unfollowButton}
            disabled={isUnfollowing}
            onClick={handleUnfollowTask}
          >
            {isUnfollowing ? 'Unfollowing...' : 'Unfollow'}
          </button>
        </div>
      </div>
      {isUnassigned && renderCapacityWarningBanner()}
    </>
  );

  const claimBanner = !isAutomaticTask && isUnassigned && !isFollowed && (
    <>
      {renderCapacityWarningBanner()}
      <div className={styles.unclaimedBanner}>
        <div className={styles.unclaimedBannerCopy}>
          <div className={styles.unclaimedLabel}>Unclaimed</div>
          <div className={styles.unclaimedText}>
            Claim to start working &middot; others will see it&apos;s taken
          </div>
        </div>
        <div className={styles.unclaimedBannerActions}>
          <span className={styles.claimButtonWrap}>
            {renderClaimNote()}
            <button type="button" {...claimButtonProps}>
              {claimButtonLabel}
            </button>
          </span>
          <button
            type="button"
            className={styles.followButton}
            disabled={isFollowing}
            onClick={handleFollowTask}
          >
            {isFollowing ? 'Following...' : 'Follow'}
          </button>
        </div>
      </div>
    </>
  );

  const followBanner = !isAutomaticTask && !isUnassigned && !isClaimedByYou && !isFollowed && (
    <div className={styles.unclaimedBanner}>
      <div className={styles.unclaimedBannerCopy}>
        <div className={styles.unclaimedLabel}>Follow this task</div>
        <div className={styles.unclaimedText}>Stay updated while someone else owns it</div>
      </div>
      <div className={styles.unclaimedBannerActions}>
        <button
          type="button"
          className={styles.followButton}
          disabled={isFollowing}
          onClick={handleFollowTask}
        >
          {isFollowing ? 'Following...' : 'Follow'}
        </button>
      </div>
    </div>
  );

  const claimedBanner = !isAutomaticTask && isClaimedByYou && !isUnassigned && (
    <div className={styles.claimedBanner}>
      <div className={styles.claimedBannerCopy}>
        <div className={styles.claimedLabel}>
          <span className={styles.claimedDot} aria-hidden="true" />
          Claimed by you
        </div>
      </div>
      <div className={styles.claimedTime}>{wasJustClaimed ? 'just now' : ''}</div>
    </div>
  );
  const waitingOnSubtasksBanner = !isCompletedTask && isWaitingOnSubtasks && (
    <div className={styles.waitingOnSubtasksBanner}>
      <div className={styles.waitingOnSubtasksIconWrap} aria-hidden="true">
        <Hourglass className={styles.waitingOnSubtasksIcon} />
      </div>
      <div className={styles.waitingOnSubtasksCopy}>
        <div className={styles.waitingOnSubtasksLabel}>
          {`Waiting on ${openDelegatedSubtasks.length} sub-task${
            openDelegatedSubtasks.length === 1 ? '' : 's'
          }`}
        </div>
        <div className={styles.waitingOnSubtasksText}>
          {`${doneDelegatedSubtasksCount} of ${delegatedSubtasks.length} complete · you'll be notified when all are done.`}
          {canceledDelegatedSubtasksCount > 0
            ? ` ${completedDelegatedSubtasksCount} completed, ${canceledDelegatedSubtasksCount} canceled.`
            : ''}
        </div>
      </div>
    </div>
  );
  const allSubtasksCompletedBanner = !isCompletedTask && areAllDelegatedSubtasksComplete && (
    <div className={styles.allSubtasksCompletedBanner}>
      <div className={styles.allSubtasksCompletedIconWrap} aria-hidden="true">
        <CheckCircle2 className={styles.allSubtasksCompletedIcon} />
      </div>
      <div className={styles.allSubtasksCompletedCopy}>
        <div className={styles.allSubtasksCompletedLabel}>No active sub-tasks remaining</div>
        <div className={styles.allSubtasksCompletedText}>
          {canceledDelegatedSubtasksCount > 0
            ? `${completedDelegatedSubtasksCount} completed, ${canceledDelegatedSubtasksCount} canceled · ready for processing.`
            : 'Ready for processing · complete this task to finish.'}
        </div>
      </div>
    </div>
  );
  const linkedTasksSection = linkedChildTasks.length > 0 && (
    <BriefRow label="Linked tasks" meta={`${linkedChildTasks.length}`} flush>
      <div className={styles.linkedTasksList}>
        {linkedChildTasks.map((linkedTask) => {
          const linkedTaskTitle = getTaskDisplayTitle(linkedTask);
          const linkedTaskDisplayStatus = getTaskDisplayStatus(linkedTask);
          const linkedTaskOwner = `${linkedTask?.owner || ''}`.trim() || 'Unassigned';
          const linkedTaskDue = formatDue(linkedTask?.due || linkedTask?.taskAssignDueAt);
          return (
            <div
              key={linkedTask.id}
              role="button"
              tabIndex={0}
              className={styles.linkedTaskRow}
              onClick={() => onSelectTask?.(linkedTask)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                onSelectTask?.(linkedTask);
              }}
            >
              <span className={styles.linkedTaskTitle}>{linkedTaskTitle}</span>
              <span className={styles.linkedTaskMeta}>
                <Badge>{linkedTaskDisplayStatus}</Badge>
                <span className={styles.linkedTaskOwner}>{linkedTaskOwner}</span>
                <span className={styles.linkedTaskDue}>{linkedTaskDue.text}</span>
              </span>
            </div>
          );
        })}
      </div>
    </BriefRow>
  );

  const subtasksSection = delegatedSubtasks.length > 0 && (
    <BriefRow
      label="Sub-tasks"
      flush
      meta={
        <>
          <span>
            {`${openDelegatedSubtasks.length} active`}
            {completedDelegatedSubtasksCount > 0
              ? ` · ${completedDelegatedSubtasksCount} completed`
              : ''}
            {canceledDelegatedSubtasksCount > 0
              ? ` · ${canceledDelegatedSubtasksCount} canceled`
              : ''}
          </span>
          {canDelegateMoreSubtasks && (
            <button
              type="button"
              className={styles.delegateMore}
              onClick={() => setShowDelegateSubtasksModal(true)}
            >
              + Delegate more
            </button>
          )}
        </>
      }
    >
      <div className={styles.subtasksList}>
        {sortedDelegatedSubtasks.map((subtask) => {
          const subtaskTitle = getTaskDisplayTitle(subtask);
          const subtaskDisplayStatus = getTaskDisplayStatus(subtask);
          const isCompletedSubtask = subtaskDisplayStatus === TASK_DISPLAY_STATUS.Done;
          const isCanceledSubtask = isTaskCanceled(subtask);
          const isCancelingSubtask = `${cancelingSubtaskId ?? ''}` === `${subtask.id ?? ''}`;
          const subtaskDue = formatDue(subtask?.due || subtask?.taskAssignDueAt);
          const subtaskTeam = teamMeta[subtask?.team];
          const subtaskTeamLabel = subtaskTeam ? `${subtaskTeam.label} team` : 'Assigned directly';
          const canCancelSubtask =
            canDelegateMoreSubtasks && !isCompletedSubtask && !isCancelingSubtask;

          return (
            <div
              key={subtask.id}
              role="button"
              tabIndex={0}
              className={`${styles.subtaskRow} ${
                isCanceledSubtask
                  ? styles.subtaskRowCanceled
                  : isCompletedSubtask
                    ? styles.subtaskRowCompleted
                    : ''
              }`}
              onClick={() => onSelectTask?.(subtask)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                onSelectTask?.(subtask);
              }}
            >
              <span
                className={`${styles.subtaskStatusDot} ${
                  isCanceledSubtask
                    ? styles.subtaskStatusDotCanceled
                    : isCompletedSubtask
                      ? styles.subtaskStatusDotCompleted
                      : ''
                }`}
                aria-hidden="true"
              />
              <span className={styles.subtaskRowPrimary}>
                <span
                  className={`${styles.subtaskTitle} ${
                    isCanceledSubtask
                      ? styles.subtaskTitleCanceled
                      : isCompletedSubtask
                        ? styles.subtaskTitleCompleted
                        : ''
                  }`}
                >
                  {subtaskTitle}
                </span>
              </span>
              <span className={styles.subtaskRowMeta}>
                <span className={styles.subtaskTeamWrap}>
                  {subtaskTeam ? (
                    <span className={styles.subtaskTeamIcon} aria-hidden="true">
                      {subtaskTeam.icon}
                    </span>
                  ) : null}
                  <span className={styles.subtaskMeta}>{subtaskTeamLabel}</span>
                </span>
              </span>
              <span
                className={`${styles.subtaskDue} ${
                  subtaskDue.urgent && !isCompletedSubtask ? styles.subtaskDueUrgent : ''
                }`}
              >
                {subtaskDue.text}
              </span>
              <button
                type="button"
                className={`${styles.subtaskTrashButton} ${
                  !canCancelSubtask ? styles.subtaskTrashButtonDisabled : ''
                }`}
                disabled={!canCancelSubtask}
                onClick={async (event) => {
                  event.stopPropagation();
                  await onCancelSubtask?.(subtask.id);
                }}
                aria-label={isCancelingSubtask ? 'Canceling sub-task' : 'Cancel sub-task'}
              >
                <Trash2 size={12} aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>
    </BriefRow>
  );
  const blockedByPrerequisiteBanner =
    isPrerequisiteBlockedTask && !isCompletedTask ? (
      <div className={styles.blockedBanner}>
        <div className={styles.blockedBannerIconWrap} aria-hidden="true">
          <Link2 className={styles.blockedBannerIcon} />
        </div>
        <div className={styles.blockedBannerCopy}>
          <div className={styles.blockedLabel}>
            {isBlockedByDependencies ? 'Blocked by prerequisites' : 'Blocked by prerequisite'}
          </div>
          {prerequisiteTaskTitle ? (
            <div className={styles.blockedWaitingText}>
              <span className={styles.blockedWaitingLabel}>
                {isBlockedByDependencies ? 'Blocked by:' : 'Waiting on:'}
              </span>{' '}
              {prerequisiteTaskTitle}
            </div>
          ) : null}
        </div>
      </div>
    ) : null;
  const completedStampText = [
    'Task',
    getClientNameById(task?.updatedBy),
    task?.updatedAt
      ? new Date(task.updatedAt).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          timeZoneName: 'short',
        })
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const completedBanner =
    isCompletedTask &&
    (isCanceledTask || isUnableToCompleteTask ? (
      <div
        className={
          isCanceledTask
            ? styles.canceledBanner
            : isUnableToCompleteTask
              ? styles.unableToCompleteBanner
              : styles.completedBanner
        }
      >
        <div
          className={
            isCanceledTask
              ? styles.canceledLabel
              : isUnableToCompleteTask
                ? styles.unableToCompleteLabel
                : styles.completedLabel
          }
        >
          <span
            className={
              isCanceledTask
                ? styles.canceledMark
                : isUnableToCompleteTask
                  ? styles.unableToCompleteMark
                  : styles.completedCheck
            }
            aria-hidden="true"
          >
            {isCanceledTask ? <Trash2 size={12} strokeWidth={2} /> : '✓'}
          </span>
          {isCanceledTask ? 'Canceled' : 'Unable to Complete'}
        </div>
      </div>
    ) : (
      <div className={styles.completedBanner}>
        <DecisionStamp label="Completed" tone="success" stamp={completedStampText} />
      </div>
    ));
  const blockedMeta = [blockedByLabel ? `Set by ${blockedByLabel}` : null, blockedAtLabel]
    .filter(Boolean)
    .join(' · ');
  const blockedStatusBanner =
    !isCompletedTask && isBlockedStatus ? (
      <Alert
        variant="destructive"
        role="status"
        flush
        inlineActions
        icon={Ban}
        title={`Blocked${blockedReasonLabel ? ` · ${blockedReasonLabel}` : ''}`}
        actions={
          <button
            type="button"
            className={styles.alertAction}
            disabled={isUnblocking}
            onClick={() => onUnblockTask?.(task.id)}
          >
            {isUnblocking ? 'Unblocking...' : 'Unblock'}
          </button>
        }
      >
        {blockedMeta || null}
      </Alert>
    ) : null;
  const escalatedMeta = [
    task?.escalatedReason,
    escalatedByLabel ? `Set by ${escalatedByLabel}` : null,
    escalatedAtLabel,
  ]
    .filter(Boolean)
    .join(' · ');
  const escalatedStatusBanner =
    !isCompletedTask && isEscalatedStatus ? (
      <Alert
        variant="warning"
        role="status"
        flush
        inlineActions
        icon={ArrowUpRight}
        title={`Escalated${escalatedToLabel ? ` → ${escalatedToLabel}` : ''}`}
        actions={
          <button
            type="button"
            className={styles.alertAction}
            disabled={isDeescalating}
            onClick={() => onDeescalateTask?.(task.id)}
          >
            {isDeescalating ? 'De-escalating...' : 'De-escalate'}
          </button>
        }
      >
        {escalatedMeta || null}
      </Alert>
    ) : null;

  if (showHuddleNotes && notesVisitId) {
    return (
      <HuddleNotesPanel
        backLabel="Back to task"
        title={taskTitle}
        notes={taskHuddleNotes}
        composerVisitLabel={showVisitLabels ? huddleVisitLabelById[notesVisitId] : undefined}
        onBack={() => setShowHuddleNotes(false)}
        onPostNote={(text) => onPostNote?.(notesVisit, text, 'task')}
        onEditNote={onEditNote}
      />
    );
  }

  const readinessBadgeLabel =
    readinessStatus === 'green'
      ? 'Ready'
      : readinessStatus === 'yellow'
        ? 'At risk'
        : readinessStatus === 'red'
          ? 'Not ready'
          : null;

  return (
    <>
      {onBackToReadiness && readinessAppointmentLabel && (
        <BackBar
          label="Back to readiness"
          title={readinessAppointmentLabel}
          onBack={onBackToReadiness}
          badge={
            readinessBadgeLabel ? (
              <Badge variant={readinessStatus} className={backBarStyles.badge}>
                {readinessBadgeLabel}
              </Badge>
            ) : null
          }
        />
      )}
      <div className={styles.root}>
        <header>
          <PanelHeadline
            className={styles.headline}
            visit={taskVisit}
            title={taskTitle}
            muted={isCompletedTask}
            subtitle={
              taskVisit ? (
                <p className={styles.taskVisitLine}>
                  <span className={styles.taskVisitName}>
                    {!taskVisit.code && (
                      <VisitCodeBadge code={null} visitType={taskVisit.kindName} />
                    )}
                    {taskVisit.label}
                  </span>
                  <span className={styles.taskVisitWhen}>{taskVisitWhen}</span>
                </p>
              ) : undefined
            }
            actions={
              <>
                <button
                  type="button"
                  onClick={handleCopyTaskLink}
                  disabled={!taskDeepLinkUrl}
                  className={styles.closeButton}
                  aria-label="Copy task link"
                  title="Copy task link"
                >
                  <Copy size={14} strokeWidth={1.8} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className={styles.closeButton}
                  aria-label="Close"
                >
                  <X size={14} strokeWidth={1.8} aria-hidden="true" />
                </button>
              </>
            }
          />
          {isCompletedTask ? completedBanner : claimedBanner}
          {systemBanner}
          {allSubtasksCompletedBanner}
          {waitingOnSubtasksBanner}
          {!isCompletedTask && followingBanner}
          {blockedStatusBanner}
          {escalatedStatusBanner}
          {!isCompletedTask && blockedByPrerequisiteBanner}
          <div className={styles.summary}>
            {taskDescription ? (
              <div className={styles.taskDescription}>{taskDescription}</div>
            ) : null}
            {task.caption && <div className={styles.caption}>{task.caption}</div>}
            <div className={styles.badgesRow}>
              {!isCompletedTask ? (
                isWaitingOnSubtasks ? (
                  <Badge className={`${styles.waitingStatusBadge} ${styles.summaryBadge}`}>
                    Waiting on sub-tasks
                  </Badge>
                ) : areAllDelegatedSubtasksComplete ? (
                  <Badge variant="green" className={styles.summaryBadge}>
                    Ready for processing
                  </Badge>
                ) : (
                  <Badge
                    variant={isUnableToCompleteTask ? 'orange' : 'default'}
                    className={styles.summaryBadge}
                  >
                    {displayStatusLabel}
                  </Badge>
                )
              ) : null}
              {pathway ? (
                <Badge variant="outline" className={styles.summaryBadge}>
                  {pathway}
                </Badge>
              ) : null}
              <span
                className={`${styles.priorityBadge} ${styles[`priorityBadge${taskPriority}`] || ''}`}
              >
                {PRIORITY_BADGE_COPY[taskPriority] ?? taskPriority}
              </span>
              {task.mustHave2026 && <Badge variant="edge">2026 Target</Badge>}
            </div>
          </div>
          <VisitSections>
            <FactSection label="Member">
              <span className={styles.memberName}>{patient?.name}</span>
              {(patient?.mrn || patient?.phase) && (
                <span className={styles.memberMeta}>
                  {patient?.mrn && <span className={styles.mrn}>{`MRN-${patient.mrn}`}</span>}
                  {patient?.mrn && patient?.phase ? ' · ' : ''}
                  {patient?.phase && `Phase ${patient.phase}`}
                </span>
              )}
              <span className={styles.linkRow}>
                {(patient || task?.patientId) && (
                  <PlatformLink line patient={patient} patientId={task?.patientId} />
                )}
                {patient && (
                  <AthenaLink
                    line
                    patient={patient}
                    integration={patient?.athenaIntegration ?? null}
                  />
                )}
                {taskIsPreVisitIntake && task?.noteUrl && (
                  <IntakeLink
                    line
                    link={task.noteUrl}
                    title={
                      task.reasonId === INTAKE_ASSIST_REQUESTED_REASON_ID
                        ? 'Unlock and open intake'
                        : task.reasonId === INTAKE_COMPLETED_REVIEW_REASON_ID
                          ? 'Open intake PDF'
                          : 'Open intake form'
                    }
                    onOpen={
                      task.reasonId === INTAKE_COMPLETED_REVIEW_REASON_ID
                        ? () => openDocument(task.noteUrl, 'pre-visit-intake.pdf')
                        : undefined
                    }
                  />
                )}
              </span>
            </FactSection>
            <FactSection label="Care team">
              {clinicianClient ? (
                <ProviderAvatars name={clinician} jobTitle="Provider" />
              ) : (
                <span className={styles.quiet}>No clinician on file</span>
              )}
            </FactSection>
            {linkedAppointment && (
              <FactSection label="Booking">
                <span className={styles.bookingMeta}>
                  {[taskVisitLocation, `${taskVisit.times.minutes} min booked`]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </FactSection>
            )}
            <FactSection label="Task">
              <dl className={styles.taskFacts}>
                <dt>Assigned to</dt>
                <dd>
                  {isUnassigned ? (
                    assignedTo
                  ) : (
                    <>
                      <strong>{currentOwner}</strong>
                      {assignedClientRole ? (
                        <span className={styles.quiet}> ({assignedClientRole})</span>
                      ) : null}
                    </>
                  )}
                </dd>
                <dt>Team</dt>
                <dd>{team ? team.label : '—'}</dd>
                <dt>Due</dt>
                <dd>
                  {task?.due
                    ? `${dayName(parseReadinessCalendarDate(dueDateText))}, ${monthDay(parseReadinessCalendarDate(dueDateText))}`
                    : dueDateText}
                  {due.text ? (
                    <>
                      {' · '}
                      <span className={due.urgent ? styles.dueUrgent : undefined}>{due.text}</span>
                    </>
                  ) : null}
                </dd>
              </dl>
              {cortexAskUrl && (
                <span className={styles.linkRow}>
                  <CortexLink line href={cortexAskUrl} />
                </span>
              )}
            </FactSection>
          </VisitSections>
        </header>

        <div className={styles.topics}>
          {notesVisitId && onPostNote ? (
            <BriefRow
              label="Huddle notes"
              meta={
                taskHuddleNotes.length
                  ? `${taskHuddleNotes.length} ${taskHuddleNotes.length === 1 ? 'note' : 'notes'}`
                  : 'Add a note'
              }
            >
              <HuddleNotesSummary
                heading={false}
                notes={taskHuddleNotes}
                onOpen={() => setShowHuddleNotes(true)}
              />
            </BriefRow>
          ) : null}
          {!(task?.parentTaskId && parentTask) && (
            <BriefRow label="Why this task exists" collapsible meta={reasonLabel}>
              <BriefItem>
                <BriefField label="Created by">{createdByLabel}</BriefField>
                <BriefField label="Reason">{reasonLabel}</BriefField>
              </BriefItem>
              {task?.evidence ? (
                <BriefItem>
                  <BriefField label="Evidence">
                    <p className={styles.evidenceSummary}>{task.evidence.summary || '—'}</p>
                  </BriefField>
                  {task.evidence.source || task.evidence.sourceRef ? (
                    <BriefField label="Source">
                      <span className={styles.evidenceSource}>{task.evidence.source}</span>
                      {task.evidence.sourceRef ? (
                        <span className={styles.evidenceSourceRef}>{task.evidence.sourceRef}</span>
                      ) : null}
                    </BriefField>
                  ) : null}
                </BriefItem>
              ) : null}
              {hasTaskSop ? (
                <BriefItem
                  actions={
                    <ItemLink
                      label="SOP"
                      describes="the standard operating procedure for this task"
                      href={taskSop}
                    />
                  }
                >
                  <span className={styles.sopText}>Standard operating procedure for this task</span>
                </BriefItem>
              ) : null}
            </BriefRow>
          )}

          {task?.parentTaskId && parentTask ? (
            <BriefRow label="Sub-task of">
              <button
                type="button"
                className={styles.parentTaskCard}
                onClick={() => onSelectTask?.(parentTask)}
              >
                <span className={styles.parentTaskIconWrap} aria-hidden="true">
                  <Hourglass className={styles.parentTaskIcon} />
                </span>
                <span className={styles.parentTaskCopy}>
                  <span className={styles.parentTaskTitle}>{parentTaskTitle}</span>
                  {parentTaskSummary ? (
                    <span className={styles.parentTaskSummary}>{parentTaskSummary}</span>
                  ) : null}
                  <span className={styles.parentTaskMeta}>
                    <span className={styles.parentTaskOwnerWrap}>
                      <span className={styles.parentTaskOwnerBadge}>
                        {parentTaskOwnerInitials || '—'}
                      </span>
                      <span>{parentTaskOwner}</span>
                    </span>
                    {parentTaskTeam?.label ? <span>{parentTaskTeam.label}</span> : null}
                    {parentTaskDueDateText ? <span>{`Due ${parentTaskDueDateText}`}</span> : null}
                  </span>
                </span>
                <ArrowUpRight
                  className={styles.parentTaskArrow}
                  strokeWidth={1.8}
                  aria-hidden="true"
                />
              </button>
            </BriefRow>
          ) : null}

          {leadResourceId ? (
            <BriefRow label="Regal call form">
              <LeadResourceCallForm embedded leadResourceId={leadResourceId} />
            </BriefRow>
          ) : null}

          {taskIsAssessment && (
            <BriefRow label="Assessment details">
              {(assessmentTypeLabel || assessmentVariantLabel || caregiverAssisted) && (
                <div className={styles.assessmentBadgesRow}>
                  {assessmentTypeLabel && (
                    <Badge variant="outline" className={styles.summaryBadge}>
                      {assessmentTypeLabel}
                    </Badge>
                  )}
                  {assessmentVariantLabel && assessmentVariantLabel !== 'Standard' && (
                    <Badge variant="outline" className={styles.summaryBadge}>
                      {assessmentVariantLabel}
                    </Badge>
                  )}
                  {caregiverAssisted && (
                    <Badge className={styles.summaryBadge}>Caregiver Assisted</Badge>
                  )}
                </div>
              )}
              <div>
                <button
                  type="button"
                  className={styles.sopLink}
                  disabled={!hasAssessmentReviewLink}
                  onClick={handleOpenAssessmentReview}
                >
                  Open in Mindy Review
                  <ArrowUpRight size={14} aria-hidden="true" />
                </button>
              </div>
            </BriefRow>
          )}

          {linkedTasksSection}

          {subtasksSection}

          {taskIsMessage && (
            <TaskConversation
              task={task}
              canReply={!isCompletedTask && isClaimedByYou}
              onReplySent={onReplySent}
            />
          )}
        </div>

        {!isCompletedTask && claimBanner}
        {!isCompletedTask && followBanner}

        {!isCompletedTask && (
          <div className={styles.sectionActions}>
            {(showCompletionActions || showCancelTask || showBlockedIndicator) && (
              <div className={styles.primaryActionSection}>
                <div className={styles.primaryActionsRow}>
                  {showBlockedIndicator && (
                    <button
                      type="button"
                      className={`${styles.primaryAction} ${styles.blockedIndicatorAction}`}
                      disabled
                      aria-disabled="true"
                    >
                      Blocked
                    </button>
                  )}
                  {showCompletionActions &&
                    (taskIsAssessment ? (
                      <>
                        <button
                          type="button"
                          className={styles.primaryAction}
                          disabled={!hasAssessmentReviewLink}
                          onClick={handleOpenAssessmentReview}
                        >
                          Start Review
                        </button>
                        <button
                          type="button"
                          className={styles.assessmentCompleteAction}
                          disabled={isMarking}
                          onClick={() => handleMarkCompleteClick()}
                        >
                          {isMarking ? 'Marking...' : 'Mark Complete'}
                        </button>
                      </>
                    ) : hasDecisionActions ? (
                      <div className={styles.decisionGroup}>
                        <ButtonGroup
                          label="Decision"
                          fullWidth
                          disabled={isMarking}
                          options={taskDecisionOptions.map((decisionOption, index) => ({
                            value: `${decisionOption.value}`,
                            label: isMarking ? 'Marking...' : decisionOption.label,
                            primary: index === 0,
                          }))}
                          onSelect={(value) => {
                            const decisionOption = taskDecisionOptions.find(
                              (option) => `${option.value}` === value,
                            );
                            handleMarkCompleteClick({
                              result: decisionOption.value,
                              decision: decisionOption.value,
                            });
                          }}
                        />
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={styles.primaryAction}
                        disabled={isMarking}
                        onClick={() => handleMarkCompleteClick()}
                      >
                        {isMarking ? 'Marking...' : 'Mark Complete'}
                      </button>
                    ))}
                  {showCancelTask && !showCancelReason && (
                    <button
                      type="button"
                      className={`${styles.secondaryAction} ${styles.cancelTaskAction} ${
                        showBlockedIndicator ? styles.cancelTaskActionFullWidth : ''
                      }`}
                      disabled={isCanceling}
                      onClick={handleCancelTaskClick}
                    >
                      {isCanceling ? 'Canceling...' : 'Cancel Task'}
                    </button>
                  )}
                </div>
                {showCancelReason && (
                  <div className={styles.cancelReasonPanel}>
                    <textarea
                      className={styles.cancelReasonInput}
                      value={cancelReason}
                      onChange={(event) => setCancelReason(event.target.value)}
                      placeholder="Reason for canceling this task"
                      rows={3}
                    />
                    <div className={styles.cancelActionRow}>
                      <button
                        type="button"
                        className={styles.secondaryAction}
                        disabled={isCanceling}
                        onClick={() => {
                          setShowCancelReason(false);
                          setCancelReason('');
                        }}
                      >
                        Keep Task
                      </button>
                      <button
                        type="button"
                        className={`${styles.secondaryAction} ${styles.cancelTaskAction}`}
                        disabled={isCanceling || !cancelReason.trim()}
                        onClick={handleCancelTaskClick}
                      >
                        {isCanceling ? 'Canceling...' : 'Confirm Cancel'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {!isAutomaticTask && (
              <div
                className={`${styles.secondaryActionSection} ${
                  taskIsMessage || isUnassigned || !showCompletionActions
                    ? styles.secondaryActionSectionStandalone
                    : ''
                }`}
              >
                <div
                  className={`${styles.secondaryActionsRow} ${
                    isClaimedByYou ? styles.secondaryActionsRowClaimed : ''
                  }`}
                >
                  <div className={styles.secondaryActionsGroup}>
                    {!isUnassigned && !isClaimedByYou && (
                      <button
                        type="button"
                        className={styles.secondaryAction}
                        onClick={() => setShowReassignModal(true)}
                      >
                        Reassign
                      </button>
                    )}
                    {isClaimedByYou && (
                      <button
                        type="button"
                        className={styles.secondaryAction}
                        disabled={isUnassigning}
                        onClick={async () => {
                          if (!task?.id) return;
                          const wasUnassigned = (await onUnassign?.(task.id)) ?? false;
                          if (!wasUnassigned) return;
                          setCurrentOwner('Unassigned');
                          setWasJustClaimed(false);
                        }}
                      >
                        {isUnassigning ? 'Unclaiming...' : 'Unclaim'}
                      </button>
                    )}
                    {(isUnassigned || isClaimedByYou) && (
                      <button
                        type="button"
                        className={styles.secondaryAction}
                        onClick={() => setShowReassignModal(true)}
                      >
                        Assign to...
                      </button>
                    )}
                    {isClaimedByYou && !task?.parentTaskId && (
                      <button
                        type="button"
                        className={styles.secondaryAction}
                        onClick={() => setShowDelegateSubtasksModal(true)}
                      >
                        Delegate sub-tasks...
                      </button>
                    )}
                  </div>
                  <div
                    className={`${styles.secondaryActionsGroup} ${
                      isClaimedByYou ? styles.secondaryActionsStatusGroupClaimed : ''
                    }`}
                  >
                    <div className={styles.statusActionWrap} ref={blockDropdownRef}>
                      <ButtonGroup
                        label="Task status"
                        options={[
                          {
                            value: 'block',
                            label: (
                              <span className={styles.statusActionLabel}>
                                <Ban size={14} aria-hidden="true" />
                                {isBlocking
                                  ? 'Blocking...'
                                  : isUnblocking
                                    ? 'Unblocking...'
                                    : isBlockedStatus
                                      ? 'Unblock'
                                      : 'Blocked'}
                              </span>
                            ),
                            active: isBlockedStatus,
                            activeTone: 'activeDanger',
                            disabled: isBlocking || isUnblocking,
                          },
                          {
                            value: 'escalate',
                            label: (
                              <span className={styles.statusActionLabel}>
                                <ArrowUpRight size={14} aria-hidden="true" />
                                {isDeescalating
                                  ? 'De-escalating...'
                                  : isEscalating
                                    ? 'Escalating...'
                                    : isEscalatedStatus
                                      ? 'De-escalate'
                                      : 'Escalate'}
                              </span>
                            ),
                            active: isEscalatedStatus,
                            activeTone: 'activeWarning',
                            disabled: isEscalating || isDeescalating,
                          },
                        ]}
                        onSelect={(value) =>
                          value === 'block'
                            ? handleBlockedButtonClick()
                            : handleEscalateButtonClick()
                        }
                      />
                      {showBlockDropdown ? (
                        <div className={styles.blockDropdown}>
                          {BLOCKED_REASON_OPTIONS.map((reason) => (
                            <button
                              key={reason.value}
                              type="button"
                              className={styles.blockDropdownItem}
                              onClick={() => handleBlockReasonSelect(reason.value)}
                            >
                              {reason.label}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {showReassignModal && (
          <ReassignModal
            task={{ ...task, owner: currentOwner }}
            onClose={() => setShowReassignModal(false)}
            isSubmitting={isReassigning}
            currentUserName={currentUserName}
            onConfirm={async (client) => {
              if (!client?.id || !task?.id) return false;

              const wasReassigned = (await onReassign?.(task.id, client.id)) ?? true;
              if (!wasReassigned) return false;

              const name =
                typeof client?.getName === 'function'
                  ? client.getName(false, true)
                  : `${client?.firstName || ''} ${client?.lastName || ''}`.trim();

              if (name) setCurrentOwner(name);
              return true;
            }}
          />
        )}
        {showDelegateSubtasksModal && (
          <DelegateSubtasksModal
            task={task}
            onClose={() => setShowDelegateSubtasksModal(false)}
            currentUserName={currentUserName}
            isSubmitting={isDelegatingSubtasks}
            onConfirm={async (rows) => (await onDelegateSubtasks?.(task, rows)) ?? false}
          />
        )}
        {showEscalateModal && (
          <EscalateTaskModal
            task={task}
            onClose={() => setShowEscalateModal(false)}
            isSubmitting={isEscalating}
            currentUserName={currentUserName}
            onConfirm={async (escalationPayload) => {
              const wasEscalated = (await onEscalateTask?.(task.id, escalationPayload)) ?? false;

              if (!wasEscalated) return false;
              return true;
            }}
          />
        )}
        {showCompleteWithSubtasksModal && (
          <CompleteWithSubtasksModal
            openCount={openDelegatedSubtasks.length}
            parentTitle={taskTitle}
            isSubmitting={isMarking}
            onClose={() => {
              setPendingCompletionPayload(null);
              setShowCompleteWithSubtasksModal(false);
            }}
            onConfirm={async () => {
              const wasCompleted =
                (await onMarkComplete?.(task.id, pendingCompletionPayload ?? undefined)) ?? false;
              if (wasCompleted) setPendingCompletionPayload(null);
              return wasCompleted;
            }}
          />
        )}
      </div>
    </>
  );
};

TaskDetail.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    owner: PropTypes.string,
    patient: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      mrn: PropTypes.string,
    }),
    patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    team: PropTypes.string,
    due: PropTypes.string,
    createdBy: PropTypes.number,
    taskAssignDueAt: PropTypes.string,
    evidence: PropTypes.shape({
      type: PropTypes.string,
      sourceRef: PropTypes.string,
      summary: PropTypes.string,
      source: PropTypes.string,
    }),
    pathway: PropTypes.string,
    departmentAthenaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    typeId: PropTypes.number,
    reasonId: PropTypes.number,
    reason: PropTypes.string,
    title: PropTypes.string,
    taskDescription: PropTypes.string,
    task_description: PropTypes.string,
    assessmentType: PropTypes.string,
    assessment_type: PropTypes.string,
    assessmentVariant: PropTypes.string,
    assessment_variant: PropTypes.string,
    caregiverAssisted: PropTypes.bool,
    caregiver_assisted: PropTypes.bool,
    caption: PropTypes.string,
    status: PropTypes.number,
    callbackStatus: PropTypes.string,
    blockedStatus: PropTypes.number,
    escalatedStatus: PropTypes.number,
    updatedBy: PropTypes.number,
    updatedAt: PropTypes.string,
    blockedBy: PropTypes.number,
    blockedAt: PropTypes.string,
    blockedReason: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    escalatedAt: PropTypes.string,
    escalatedBy: PropTypes.number,
    escalatedTo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    escalatedToUser: PropTypes.number,
    escalatedReason: PropTypes.string,
    previousAssigneeId: PropTypes.number,
    unblockedBy: PropTypes.number,
    deescalatedBy: PropTypes.number,
    priority: PropTypes.string,
    isVisible: PropTypes.bool,
    stepType: PropTypes.string,
    dependency: PropTypes.object,
    mustHave2026: PropTypes.bool,
    isFollowing: PropTypes.bool,
    followedBy: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    noteUrl: PropTypes.string,
    taskSop: PropTypes.string,
    task_sop: PropTypes.string,
    threadId: PropTypes.string,
    externalObjectType: PropTypes.string,
    externalObjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    parentTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    workflowInstanceId: PropTypes.string,
  }),
  relatedTasks: PropTypes.arrayOf(PropTypes.object),
  cachedDelegatedSubtasks: PropTypes.arrayOf(PropTypes.object),
  onClose: PropTypes.func,
  onBackToReadiness: PropTypes.func,
  readinessAppointmentLabel: PropTypes.string,
  readinessStatus: PropTypes.oneOf(['default', 'green', 'yellow', 'red']),
  onAssignToMe: PropTypes.func,
  isAssigning: PropTypes.bool,
  onUnassign: PropTypes.func,
  isUnassigning: PropTypes.bool,
  onMarkComplete: PropTypes.func,
  isMarking: PropTypes.bool,
  onCancelTask: PropTypes.func,
  isCanceling: PropTypes.bool,
  onReassign: PropTypes.func,
  isReassigning: PropTypes.bool,
  onFollowTask: PropTypes.func,
  isFollowing: PropTypes.bool,
  onUnfollowTask: PropTypes.func,
  isUnfollowing: PropTypes.bool,
  onBlockTask: PropTypes.func,
  isBlocking: PropTypes.bool,
  onUnblockTask: PropTypes.func,
  isUnblocking: PropTypes.bool,
  onEscalateTask: PropTypes.func,
  isEscalating: PropTypes.bool,
  onDeescalateTask: PropTypes.func,
  isDeescalating: PropTypes.bool,
  onDelegateSubtasks: PropTypes.func,
  isDelegatingSubtasks: PropTypes.bool,
  onCancelSubtask: PropTypes.func,
  cancelingSubtaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentUserName: PropTypes.string,
  cortexOrigin: PropTypes.string,
  onReplySent: PropTypes.func,
  onSelectTask: PropTypes.func,
  departmentLabelById: PropTypes.object,
};

export default TaskDetail;
